import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The single canonical explanation of what "Elapsed" means on the dashboards.
 *
 * `durationMinutes` is computed as `completedAt - startedAt` with no idle
 * handling (see `calculateDurationMinutes` in the API's progressService), so
 * the figure absorbs idle time, queueing, overnight gaps and weekends. It is
 * elapsed wall-clock time, not effort and not active working time. Displaying
 * it without this explanation is what made the metric misleading in the first
 * place, so the text is rendered as visible inline copy rather than hidden
 * behind a hover-only tooltip.
 */
export const ELAPSED_TIME_HELPER_TEXT =
  "Elapsed is wall-clock time from when work started to when it completed. It includes idle time, queueing, overnight gaps and weekends, so it is not a measure of effort or active working time.";

/**
 * The measured coverage caveat.
 *
 * Deliberately TIMELESS: it states that a coverage gap exists and why, without
 * naming percentages. The original wording carried figures measured once
 * against one development database (PI-SPEC-SW-981's gating query, recorded via
 * log_decision on epic PI-SPEC-SW-E205: 1,966 of 2,050 completed features and
 * 4,190 of 5,695 completed tasks carried an elapsed value). Rendering those
 * numbers as present-tense fact in every deployment was wrong twice over — they
 * drift the moment a record is added, and they were never true of any database
 * but that one. Anyone who needs the current figure should query for it.
 *
 * The gap itself is structural and will not close: it consists of items
 * completed without ever having been started, which have no start timestamp to
 * subtract from and therefore cannot be backfilled.
 */
export const ELAPSED_TIME_COVERAGE_TEXT =
  "It also excludes work completed without a recorded start: those items have no start timestamp to measure from and cannot be backfilled, so totals cover only the work that carries both timestamps.";

interface ElapsedTimeNoteProps {
  className?: string;
  /** Omit the measured-coverage sentence (e.g. in tight layouts). */
  withoutCoverage?: boolean;
}

/**
 * Inline, always-rendered helper text explaining the Elapsed metric.
 *
 * Deliberately NOT a tooltip: Radix tooltip content is portal-rendered only
 * while open, so it is unavailable to a screen reader that has not focused the
 * trigger. This text is unconditionally in the accessibility tree and reachable
 * by keyboard because it needs no interaction at all.
 */
export function ElapsedTimeNote({ className, withoutCoverage }: ElapsedTimeNoteProps) {
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground",
        className,
      )}
      data-testid="elapsed-time-note"
    >
      <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      <span>
        {ELAPSED_TIME_HELPER_TEXT}
        {!withoutCoverage && ` ${ELAPSED_TIME_COVERAGE_TEXT}`}
      </span>
    </p>
  );
}
